/**
 * ENG-4862 — Shared agent-liveness derivation.
 *
 * Single source of truth for "is this agent reachable?". Used by:
 *   - packages/api  — to populate the `liveness` field in /agents and
 *     /agents/:id/heartbeat responses.
 *   - packages/webapp — as a fallback when the API response doesn't yet
 *     include the field (older deploy, race during rollout, etc.).
 *
 * Pre-ENG-4857 the only signal was heartbeat freshness. That produced a
 * false positive when the host process was alive but its Claude session
 * wasn't authenticated. The four-state enum lets the UI distinguish:
 *
 *   - 'online'        — heartbeat fresh AND host's Claude is authenticated
 *   - 'auth_blocked'  — heartbeat fresh BUT Claude is not_authenticated/expired
 *   - 'offline'       — heartbeat is stale (or host is missing)
 *   - 'never'         — agent has never reported a heartbeat
 *
 * `hostClaudeAuthStatus` may be null when the agent has no host
 * assignment yet, OR when an older API response didn't include the
 * field. Null is treated as "unknown — don't downgrade to auth_blocked"
 * so consumers that haven't been wired through still report the
 * pre-ENG-4857 behaviour.
 */

export const FRESH_HEARTBEAT_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes — matches existing UI conventions

export type AgentLiveness = 'online' | 'auth_blocked' | 'offline' | 'never';

export interface LivenessInputs {
  lastHeartbeatAt: string | null | undefined;
  /** Host's claude_auth_status: 'valid' | 'expired' | 'not_authenticated' | null */
  hostClaudeAuthStatus?: string | null;
  /**
   * ENG-7112 — the assigned host's `last_seen_at`. This is bumped on every
   * authenticated host request (near-realtime), whereas the per-agent
   * `last_heartbeat_at` is only bumped inside the manager's single poll loop
   * and therefore tracks poll-loop cadence, not real reachability. On a busy
   * host an idle agent's heartbeat can lag past the staleness threshold while
   * the host is plainly alive, flapping the agent to 'offline'. When the host
   * is demonstrably fresh we treat that as an equivalent liveness signal so
   * the lag alone can't mark an alive agent offline. null/omitted ⇒ no host
   * signal (pre-ENG-7112 behaviour: heartbeat freshness is the only input).
   */
  hostLastSeenAt?: string | null;
  /** Override the freshness threshold (ms). Tests use this. */
  thresholdMs?: number;
  /** Override the host-freshness threshold (ms). Defaults to `thresholdMs`. */
  hostThresholdMs?: number;
  /** Optional clock injection for tests. */
  now?: number;
}

export interface LivenessResult {
  liveness: AgentLiveness;
  /** Human-readable explanation of WHY the state was chosen. Useful for tooltips and UI banners. */
  reason: string;
}

const REASONS: Record<AgentLiveness, string> = {
  online: 'Online',
  auth_blocked: "Host alive — Claude not authenticated, agent can't reply",
  offline: 'Offline (heartbeat stale)',
  never: 'Never seen',
};

/**
 * Derive an agent's liveness state. Returns just the enum; use
 * `deriveLiveness` to also get a `reason` string in one call.
 */
export function getAgentLiveness({
  lastHeartbeatAt,
  hostClaudeAuthStatus,
  hostLastSeenAt,
  thresholdMs = FRESH_HEARTBEAT_THRESHOLD_MS,
  hostThresholdMs,
  now = Date.now(),
}: LivenessInputs): AgentLiveness {
  // 'never' stays keyed on the per-agent heartbeat only: an agent that has
  // never reported is genuinely never-seen, regardless of host chatter.
  if (!lastHeartbeatAt) return 'never';
  const heartbeatFresh = now - new Date(lastHeartbeatAt).getTime() < thresholdMs;

  // ENG-7112: rescue an alive agent from a false 'offline'. `last_heartbeat_at`
  // is bumped once per manager poll-loop iteration, so it lags poll cadence and
  // can cross the threshold while the agent is idle on a busy host. The host's
  // `last_seen_at` is bumped on every authenticated host request (near-real
  // time), so a fresh host is strong evidence the manager — and thus the
  // agent's runtime — is still reachable. Treat it as an equivalent fresh
  // signal. (An invalid/NaN date yields `false`, so a bad value can't rescue.)
  const hostFresh =
    hostLastSeenAt != null &&
    now - new Date(hostLastSeenAt).getTime() < (hostThresholdMs ?? thresholdMs);

  if (!heartbeatFresh && !hostFresh) return 'offline';

  // Heartbeat (or host) is fresh. If we know the Claude auth status and it's
  // not 'valid', the agent can't actually reply — surface that distinct state.
  // null means "we don't have the signal" — fall back to 'online' to avoid
  // regressing callers that haven't been wired to pass it yet.
  if (hostClaudeAuthStatus != null && hostClaudeAuthStatus !== 'valid') {
    return 'auth_blocked';
  }
  return 'online';
}

/** Returns both the enum and a tooltip-ready reason in one call. */
export function deriveLiveness(inputs: LivenessInputs): LivenessResult {
  const liveness = getAgentLiveness(inputs);
  return { liveness, reason: describeLiveness(liveness, inputs) };
}

/** Convenience boolean for callers that only care about "can message it now?" */
export function isAgentReachable(inputs: LivenessInputs): boolean {
  return getAgentLiveness(inputs) === 'online';
}

/** Human-readable label for tooltips and status banners. */
export function describeLiveness(
  liveness: AgentLiveness,
  inputs?: Pick<LivenessInputs, 'hostClaudeAuthStatus'>,
): string {
  if (liveness === 'auth_blocked' && inputs?.hostClaudeAuthStatus === 'expired') {
    return "Host alive — Claude authentication has expired, agent can't reply";
  }
  return REASONS[liveness];
}
